import { useEffect, useState } from "react";
import { ListGroup, Form, InputGroup, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import SatAPI from "../../../../_services/sat";
import toastService from "../../../../_services/toastService";
import { useSelector } from "react-redux";
import { getContrastColor } from "../../../../_helpers/contrast";

const api = SatAPI();

const makeOlaPrefGroups = (success, warning) => [
  { key: "high",       label: `Hoge voorkeur (${success}-10)`,                variant: "success",   test: c => c.hoewistExpertiseFit == null && c.hoewistScore >= success,                                          badge: c => `${c.hoewistScore}/10` },
  { key: "mid",        label: `Neutrale voorkeur (${warning}-${success - 1})`, variant: "warning",  test: c => c.hoewistExpertiseFit == null && c.hoewistScore >= warning && c.hoewistScore < success,              badge: c => `${c.hoewistScore}/10` },
  { key: "low",        label: `Lage voorkeur (1-${warning - 1})`,              variant: "danger",    test: c => c.hoewistExpertiseFit == null && c.hoewistScore != null && c.hoewistScore > 0 && c.hoewistScore < warning, badge: c => `${c.hoewistScore}/10` },
  { key: "no_pref",    label: "Geen voorkeur",                                 variant: "secondary", test: c => c.hoewistExpertiseFit == null && c.hoewistScore === 0,                                               badge: () => "Geen voorkeur"       },
  { key: "bijscholen", label: "Wil bijscholen",                                 variant: null,        test: c => c.hoewistExpertiseFit === 1,                                                                          badge: () => null                  },
  { key: "buiten",     label: "Buiten interessegebied",                        variant: null,        test: c => c.hoewistExpertiseFit === 2,                                                                          badge: () => null                  },
  { key: "none",       label: "Geen voorkeur opgegeven",                       variant: null,        test: c => c.hoewistExpertiseFit == null && c.hoewistScore == null,                                              badge: () => null                  },
];

const TASK_PREF_GROUPS = [
  { key: "yes",  label: "Wil deze taak graag uitvoeren", variant: "success", test: c => c.hoewistWantsToDo === true,  badge: () => "Wil graag"   },
  { key: "no",   label: "Voert deze taak liever niet uit", variant: "danger",  test: c => c.hoewistWantsToDo === false, badge: () => "Liever niet" },
  { key: "none", label: "Geen voorkeur opgegeven", variant: null,      test: c => c.hoewistWantsToDo == null,   badge: () => null          },
];

const phaseColorBadgeEnabled = localStorage.getItem("SAT-phase-color-badge") !== "false";

function DocentList({ selectDocent, programme, ola, olaId, phaseId, phaseColor, taskId }) {
  const snapshot = useSelector(state => state.snapshot.fileName);
  const [collegasIP, setCollegasIP] = useState([]);
  const [collegasOther, setCollegasOther] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [hoewistActive, setHoewistActive] = useState(false);
  const [thresholdSuccess, setThresholdSuccess] = useState(7);
  const [thresholdWarning, setThresholdWarning] = useState(4);

  const onDocentClick = docent => {
    selectDocent(docent);
  };

  const load = async () => {
    try {
      setLoading(true);
      const [response, globalSettings, academicYears] = await Promise.all([
        api.getActiveCollegas(snapshot),
        api.getGlobalSettings().catch(() => null),
        api.getAcademicYears().catch(() => []),
      ]);
      const currentAcademicYear = (academicYears || [])
        .sort((a, b) => b.year.localeCompare(a.year))
        .find(y => y.current_year) ?? null;
      if (globalSettings?.preferenceScoreHigh != null) setThresholdSuccess(globalSettings.preferenceScoreHigh);
      if (globalSettings?.preferenceScoreLow != null) setThresholdWarning(globalSettings.preferenceScoreLow);
      if (localStorage.getItem("SAT-typeSorting") === "firstname") {
        response.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName));
      } else {
        response.sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
      }

      response.filter(c => {
        console.log(c.programmes);
        return true;
      });

      const inProgram = response.filter(c => c.programmes.filter(p => p.programme.ID === programme.ID).length > 0);
      const other = response.filter(c => c.programmes.filter(p => p.programme.ID === programme.ID).length === 0);

      inProgram.forEach(collega => {
        collega.show = true;
        if (ola) {
          ola.assignments.forEach(assignment => {
            if (collega.ID === assignment.collegaID) {
              collega.show = false;
            }
          })
        }
      })

      other.forEach(collega => {
        collega.show = true;
        if (ola) {
          ola.assignments.forEach(assignment => {
            if (collega.ID === assignment.collegaID) {
              collega.show = false;
            }
          })
        }
      })

      const useHoewist = localStorage.getItem("SAT-hoewist-sorting") === "true" && (olaId || taskId);

      if (useHoewist) {
        const subResults = await Promise.all(
          inProgram.map(async c => {
            try {
              const subs = await api.getHoewistSubmissionsByCollegaId(c.ID);
              const sub = Array.isArray(subs) && subs.length > 0
                ? (currentAcademicYear ? (subs.find(s => s.academicYearId === currentAcademicYear.id) ?? null) : subs[0])
                : null;
              return { collegaId: c.ID, sub };
            } catch (_) {
              return { collegaId: c.ID, sub: null };
            }
          })
        );

        const prefResults = await Promise.all(
          subResults.filter(r => r.sub).map(async ({ collegaId, sub }) => {
            try {
              if (olaId) {
                const [olaPrefs, phasePrefs] = await Promise.all([
                  api.getOlaPreferencesBySubmissionId(sub.id),
                  phaseId ? api.getPhasePreferencesBySubmissionId(sub.id) : Promise.resolve([]),
                ]);
                const pref = (olaPrefs || []).find(p => p.olaId === olaId) ?? null;
                const phasePref = phaseId ? ((phasePrefs || []).find(p => p.phaseId === phaseId) ?? null) : null;
                return {
                  collegaId,
                  hoewistScore: pref?.passionScore ?? null,
                  hoewistExpertiseFit: pref?.expertiseFit ?? null,
                  hoewistComment: pref?.comment || null,
                  hoewistPhaseScore: phasePref?.passionScore ?? null,
                  hoewistPhaseComment: phasePref?.comment || null,
                };
              } else {
                const prefs = await api.getTaskPreferencesBySubmissionId(sub.id);
                const pref = (prefs || []).find(p => p.taskId === taskId) ?? null;
                return { collegaId, hoewistWantsToDo: pref?.wantsToDo ?? null, hoewistComment: pref?.comment || null };
              }
            } catch (_) {
              return { collegaId };
            }
          })
        );

        const prefMap = {};
        prefResults.forEach(r => { prefMap[r.collegaId] = r; });

        inProgram.forEach(c => {
          const result = prefMap[c.ID] ?? {};
          if (olaId) {
            c.hoewistScore = result.hoewistScore ?? null;
            c.hoewistExpertiseFit = result.hoewistExpertiseFit ?? null;
            c.hoewistComment = result.hoewistComment ?? null;
            c.hoewistPhaseScore = result.hoewistPhaseScore ?? null;
            c.hoewistPhaseComment = result.hoewistPhaseComment ?? null;
          } else {
            c.hoewistWantsToDo = result.hoewistWantsToDo ?? null;
            c.hoewistComment = result.hoewistComment ?? null;
          }
        });
      } else {
        inProgram.forEach(c => { c.hoewistScore = null; c.hoewistExpertiseFit = null; c.hoewistPhaseScore = null; c.hoewistWantsToDo = null; });
      }

      setCollegasIP(inProgram);
      setCollegasOther(other);
      setHoewistActive(!!useHoewist);
      setLoading(false);
    } catch (error) {
      toastService.send({ title: "An error occured", message: error.toString() });
    }
  };

  const filter = list =>
    list.filter(c => {
      if (search === "") {
        return true;
      }
      return `${c.firstName} ${c.lastName} ${c.firstName} `.toLowerCase().includes(search.toLowerCase()); // clever trick to search on first and last name
    });

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }

  return (
    <div>
      <InputGroup className="mb-3">
        <InputGroup.Text id="basic-addon1">
          <i className="fas fa-search" />
        </InputGroup.Text>
        <Form.Control autoFocus type="text" required onChange={e => setSearch(e.target.value, 10)} value={search} />
      </InputGroup>
      <p className="mb-1">Docenten {programme.name}</p>
      {hoewistActive ? (
        (olaId ? makeOlaPrefGroups(thresholdSuccess, thresholdWarning) : TASK_PREF_GROUPS).map(({ key, label, variant, test, badge }) => {
          const members = filter(collegasIP).filter(c => c.show && test(c));
          if (members.length === 0) return null;
          return (
            <div key={key}>
              <p className="mb-1 mt-2 hoewist-sort-group-label text-muted">{label}</p>
              <ListGroup className="mb-1">
                {members.map(docent => (
                  <ListGroup.Item key={docent.ID} action onClick={() => onDocentClick(docent)}>
                    {(docent.hoewistComment || docent.hoewistPhaseComment) && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip style={{ maxWidth: 300 }}>
                            {docent.hoewistComment && (
                              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", textAlign: "left" }}>
                                {docent.hoewistComment}
                              </div>
                            )}
                            {docent.hoewistPhaseComment && (
                              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", textAlign: "left", marginTop: docent.hoewistComment ? 4 : 0 }}>
                                <strong>Fase:</strong> {docent.hoewistPhaseComment}
                              </div>
                            )}
                          </Tooltip>
                        }
                      >
                        <span className="text-info me-1">
                          <i className="far fa-comment-dots" />
                        </span>
                      </OverlayTrigger>
                    )}
                    {docent.firstName} {` `}
                    {docent.lastName}
                    {variant && badge(docent) && (
                      <Badge bg={variant} className="float-end ms-1" title="Vakvoorkeur">
                        {olaId ? `Vak ${badge(docent)}` : badge(docent)}
                      </Badge>
                    )}
                    {olaId && docent.hoewistPhaseScore != null && (() => {
                      if (docent.hoewistPhaseScore === 0) {
                        return phaseColorBadgeEnabled ? (
                          <Badge bg={null} className="float-end ms-1" style={{ backgroundColor: phaseColor, color: getContrastColor(phaseColor) }} title="Fasevoorkeur">
                            Fase: geen voorkeur
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="float-end ms-1" title="Fasevoorkeur">
                            Fase: geen voorkeur
                          </Badge>
                        );
                      }
                      if (phaseColorBadgeEnabled) {
                        return (
                          <Badge bg={null} className="float-end ms-1" style={{ backgroundColor: phaseColor, color: getContrastColor(phaseColor) }} title="Fasevoorkeur">
                            Fase {docent.hoewistPhaseScore}/10
                          </Badge>
                        );
                      }
                      const phaseVariant = docent.hoewistPhaseScore >= thresholdSuccess ? "success"
                        : docent.hoewistPhaseScore >= thresholdWarning ? "warning"
                        : "danger";
                      return (
                        <Badge bg={phaseVariant} className="float-end ms-1" title="Fasevoorkeur">
                          Fase {docent.hoewistPhaseScore}/10
                        </Badge>
                      );
                    })()}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          );
        })
      ) : (
        <ListGroup>
          {filter(collegasIP, programme.ID).map(docent => (
            <ListGroup.Item key={docent.ID} action onClick={() => onDocentClick(docent)} className={docent.show ? "" : "d-none"}>
              {docent.firstName} {` `}
              {docent.lastName}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      <p className="mb-1">Overige Docenten</p>
      <ListGroup>
        {filter(collegasOther).map(docent => (
          <ListGroup.Item key={docent.ID} action onClick={() => onDocentClick(docent)} className={docent.show ? "" : "d-none"}>
            {docent.firstName} {` `}
            {docent.lastName}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
}

export default DocentList;
